import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "./App.css";

const API_BASE = "api"; // Ensure this is your actual Vercel backend URL

function App() {
  const [step, setStep] = useState("login"); // Initial state is 'login'
  const [form, setForm] = useState({ username: "", password: "", mfa: "" });
  const [stayLoggedIn, setStayLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [message, setMessage] = useState({ text: "", type: "" }); // For custom messages

  // Helper function to display custom messages
  // Memoized with useCallback as it's used by other memoized functions
  const showMessage = useCallback((text, type = "info") => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 8000);
  }, []); // No dependencies, as it only uses `setMessage` (stable) and `setTimeout`

  // Helper function to clear authentication state
  // Memoized with useCallback as it's used by other memoized functions
  const clearAuth = useCallback((showExpiredMsg = false) => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("authToken");
    setStep("login");
    setForm({ username: "", password: "", mfa: "" });
    if (showExpiredMsg) {
      showMessage("Your session has expired or is invalid. Please log in again.", "info");
    }
  }, [showMessage]); // Depends on showMessage, so include it

  // Function to check authentication status and fetch user data
  // Memoized with useCallback to provide a stable function identity for useEffect
  const checkAuth = useCallback(async (currentToken) => {
    if (!currentToken) {
      // No token at all — just go to login silently, no expired message
      setStep("login");
      return;
    }
    try {
      axios.defaults.headers.common["Authorization"] = `Bearer ${currentToken}`;
      const res = await axios.get(`${API_BASE}/me`);
      if (res.data.user) {
        setUser(res.data.user);
        setStep("dashboard");
        showMessage("Welcome back!", "success");
      } else {
        // Token exists but server returned no user — session is invalid
        clearAuth(true);
      }
    } catch (err) {
      console.error("Token revalidation failed:", err);
      clearAuth(true); // Token exists but is rejected — show expired message
    }
  }, [clearAuth, showMessage]); // Dependencies: clearAuth and showMessage (both memoized)

  // Effect to initialize token from storage and revalidate
  // This runs once on mount. If a token is found, it calls checkAuth.
  // checkAuth is a dependency because it's a function defined within the component scope.
  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      setToken(storedToken);
      checkAuth(storedToken);
    } else {
      setStep("login"); // No token found, go to login silently
    }
  }, [checkAuth]); // Crucial fix: checkAuth is a dependency here

  // Effect to set or clear Authorization header for Axios
  // This runs whenever the 'token' state changes.
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, [token]); // Dependency: token

  // Handles form input changes
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Handles user login
  const login = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API_BASE}/login`, {
        username: form.username,
        password: form.password,
        stayLoggedIn,
      });

      if (res.data.mfa) {
        setStep("mfa");
        showMessage("MFA required. Please enter your code.", "info");
      } else if (res.data.user) {
        // MFA disabled: server returns user only. MFA enabled post-verify: also includes token.
        setUser(res.data.user);
        if (res.data.token) {
          setToken(res.data.token);
          localStorage.setItem("token", res.data.token);
        }
        setStep("dashboard");
        showMessage("Login successful!", "success");
      }
    } catch (err) {
      console.error("Login error:", err);
      showMessage("Login failed. Check username/password.", "error");
    }
  };

  // Handles MFA verification
  const submitMFA = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API_BASE}/mfa`, {
        username: form.username,
        code: form.mfa,
      });

      if (res.data.user && res.data.token) {
        setUser(res.data.user);
        setToken(res.data.token);

        localStorage.setItem("token", res.data.token);

        setStep("dashboard");
        showMessage("MFA verified. Welcome!", "success");
      }
    } catch (err) {
      console.error("MFA error:", err);
      showMessage("Invalid MFA code.", "error");
    }
  };

  // Handles disabling MFA
  const disableMFA = async () => {
    try {
      await axios.post(`${API_BASE}/disable-mfa`);
      showMessage("MFA disabled successfully.", "success");
    } catch (err) {
      console.error("Disable MFA error:", err);
      showMessage("Failed to disable MFA.", "error");
    }
  };

    const enableMFA = async () => {
    try {
      await axios.post(`${API_BASE}/enable-mfa`);
      showMessage("MFA enabled successfully.", "success");
    } catch (err) {
      console.error("Enable MFA error:", err);
      showMessage("Failed to enable MFA.", "error");
    }
  };


  // Handles user logout
  const logout = async () => {
    try {
      await axios.post(`${API_BASE}/logout`);
    } catch (err) {
      console.error("Logout error (might be expected for stateless logout):", err);
    }
    clearAuth(false); // Use the memoized clearAuth helper function
    showMessage("You have been logged out.", "info");
  };

  return (
    <div className="akira-bg">
      <div className="logo-container">
        <img
          src="https://cdn.prod.website-files.com/69b3cac005124285ea612325/69b794936957731b3595ac5b_Redfox%20Cybersecurity%20logo%20v2.svg"
          alt="redfox Logo"
          className="akira-logo"
        />
      </div>

      {/* Custom Message Box for better UX than alerts */}
      {message.text && (
        <div className={`message-box ${message.type}`}
        style={{ color: message.color || "White", fontWeight: "bold" }}>
          {message.text}
        </div>
      )}

      {/* The app starts directly at the login step if no token is found */}
      {step === "login" && (
        <form className="auth-box" onSubmit={login}>
          <h2>RedFox</h2>
          <input
            name="username"
            placeholder="Username"
            value={form.username}
            onChange={handleChange}
            required
            autoFocus
          />
          <input
            type="password"
            name="password"
            placeholder="Password"
            value={form.password}
            onChange={handleChange}
            required
          />

          <label className="stay-logged-in-label">
            <input
              type="checkbox"
              checked={stayLoggedIn}
              onChange={() => setStayLoggedIn((prev) => !prev)}
            />{" "}
            Stay Logged In
          </label>
          <button type="submit"> Login </button>
        </form>
      )}

      {step === "mfa" && (
        <form className="auth-box" onSubmit={submitMFA}>
          <h2>Enter your MFA code</h2>
          <input
            name="mfa"
            placeholder="MFA Code"
            value={form.mfa}
            onChange={handleChange}
            required
            autoFocus
          />
          <button type="submit">SUBMIT</button>
        </form>
      )}

      {step === "dashboard" && user && (
        <div className="auth-box dashboard-box">
          <h2>Welcome back!</h2>
          <div className="user-info">
            <div>👤 {user.username}</div>
            <div>✉️ {user.email}</div>
            <div>🆔 {user.id}</div>
          </div>
          <button className="danger" onClick={disableMFA}>
            DISABLE MFA
          </button>
          <button className="danger" onClick={enableMFA}>
            ENABLE MFA
          </button>
          <button onClick={logout}>LOGOUT</button>
        </div>
      )}
    </div>
  );
}

export default App;